import axios from 'axios';

// Enable credentials for all axios requests (cookies/auth)
axios.defaults.withCredentials = true;

// Use relative path so it works in both dev and production
const API_BASE_URL = '/api/v1';

/** Kandidat, dem eine E-Mail bereits gehört (Antwort 409 / check-email). */
export interface VorhandenerKandidat { id: string; name: string; candidate_number: string | null; }

export interface Candidate {
  id: string;
  company_id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;

  // Extended Personal Info (NEW)
  salutation: string | null; // Herr/Frau/Divers
  candidate_number: string | null; // PHE-XXXXX format

  // Location (Extended with street/postal_code)
  street: string | null;
  postal_code: string | null;
  city: string | null;
  state: string | null; // Bundesland
  country: string | null;

  // LinkedIn & Social
  linkedin_url: string | null;

  // Photos & Files
  resume_url: string | null;
  photo_url: string | null;

  // Professional Info
  current_position: string | null;
  current_company: string | null;
  total_years_experience: number | null;
  years_experience: number | null; // Alias for DB field

  // Skills & Education
  skills: string[];
  languages: any[];
  education: any[];
  experience: any[];

  // Extended Status & Details (NEW from Salesforce)
  status: string;
  /** Führendes Statusfeld — siehe constants/candidateStage */
  stage?: string | null;
  /** @deprecated seit Migration 041_01 — Inhalte auf stage/dnc/tags verteilt */
  talent_status: string | null;
  availability: string | null; // Sofort/3 Monate/etc.
  salary_expectation: string | null; // Gehaltsvorstellung
  travel_willingness: string | null; // Reisebereitschaft %
  shift_readiness: string | null; // Schichtbereitschaft Ja/Nein
  internal_classification: string | null; // Interne Klassifizierung
  description: string | null; // Beschreibung/Interne Informationen

  // Tags & Notes
  tags: string[];
  notes: string | null;
  source: string;
  is_favorite: boolean;

  // AI Analysis
  ai_summary: string | null;

  // Timestamps
  created_at: string;
  updated_at: string;
  created_by: string | null;

  // Salesforce Integration
  salesforce_id: string | null;
  salesforce_data: {
    source?: string;
    imported_at?: string;
    salesforce_id?: string;
    [key: string]: any; // Additional custom fields from Salesforce
  } | null;

  // Document tracking
  documents_imported: boolean;
  merged_from: string | null;
  company_ref_id: string | null;

  // User who is responsible for this candidate
  edited_by: string | null;
}

export interface CandidateDocument {
  id: string;
  name: string;
  type: string;
  file_url: string;
  file_size: number;
  mime_type: string;
  created_at: string;
}

class CandidatesService {
  /**
   * Get candidates with pagination
   * Backend now caps at 500 per request to prevent timeout
   * @param limit - Number of candidates to fetch (max 500)
   * @param offset - Offset for pagination
   * @param search - Search query
   * @param letter - A-Z filter
   * @param sortBy - Sort field (created_at, last_name, first_name, city, candidate_number)
   * @param sortOrder - Sort order ('asc' or 'desc')
   */
  async getCandidates(
    limit = 500,
    offset = 0,
    search = '',
    letter = '',
    sortBy = 'last_name',
    sortOrder = 'asc'
  ): Promise<{ success: boolean; data: Candidate[] }> {
    try {
      // Cap limit to backend max of 500
      const safeLimit = Math.min(limit, 500);
      console.log(`📞 Fetching candidates: limit=${safeLimit}, offset=${offset}, sortBy=${sortBy}, sortOrder=${sortOrder}`);
      const response = await axios.get(`${API_BASE_URL}/candidates`, {
        params: {
          limit: safeLimit,
          offset,
          ...(search && { search }),
          ...(letter && { letter }),
          sortBy,
          sortOrder
        }
      });
      console.log(`✅ Received ${response.data.data?.length || 0} candidates from API`);
      return response.data;
    } catch (error: any) {
      console.error('❌ Error fetching candidates:');
      console.error('  Message:', error.message);
      console.error('  Code:', error.code);
      console.error('  Response:', error.response?.data);
      console.error('  Status:', error.response?.status);
      console.error('  Full error:', error);
      throw error;
    }
  }

  /**
   * Get ALL candidates (using pagination to avoid timeout)
   * Loads in batches of 50 to prevent Supabase statement timeout
   */
  async getAllCandidates(): Promise<{ success: boolean; data: Candidate[]; total: number }> {
    console.log('🔄 candidatesService: Loading ALL candidates with pagination...');

    // REDUCED batch size - Supabase times out with larger batches
    const BATCH_SIZE = 50;
    let allCandidates: Candidate[] = [];
    let offset = 0;
    let hasMore = true;
    let batchCount = 0;

    while (hasMore) {
      batchCount++;
      console.log(`📦 Fetching batch ${batchCount}: offset=${offset}, limit=${BATCH_SIZE}`);

      try {
        const result = await this.getCandidates(BATCH_SIZE, offset);

        if (!result.success || !result.data || result.data.length === 0) {
          hasMore = false;
        } else {
          allCandidates.push(...result.data);
          offset += BATCH_SIZE;

          // If we got fewer than BATCH_SIZE, we've reached the end
          if (result.data.length < BATCH_SIZE) {
            console.log(`📦 Batch ${batchCount} returned ${result.data.length} < ${BATCH_SIZE}, last batch`);
            hasMore = false;
          }

          // Safety limit: max 150 batches (7,500 candidates)
          if (batchCount >= 150) {
            console.log('⚠️ Reached safety limit of 150 batches (7,500 candidates)');
            hasMore = false;
          }
        }
      } catch (error) {
        console.error(`❌ Error in batch ${batchCount}:`, error);
        // Stop on error but return what we have
        hasMore = false;
      }
    }

    console.log(`✅ Loaded ${allCandidates.length} total candidates in ${batchCount} batches`);

    return {
      success: true,
      data: allCandidates,
      total: allCandidates.length
    };
  }

  /**
   * Get single candidate by ID
   */
  async getCandidate(id: string): Promise<{ success: boolean; data: Candidate }> {
    try {
      const response = await axios.get(`${API_BASE_URL}/candidates/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching candidate:', error);
      throw error;
    }
  }

  /**
   * Get candidate documents (PDFs, etc.)
   */
  async getCandidateDocuments(id: string): Promise<{ success: boolean; data: CandidateDocument[] }> {
    try {
      const response = await axios.get(`${API_BASE_URL}/candidates/${id}/documents`);
      return response.data;
    } catch (error) {
      console.error('Error fetching candidate documents:', error);
      throw error;
    }
  }

  /**
   * Analyze candidate PDF with AI
   */
  async analyzeCandidatePDF(id: string): Promise<{ success: boolean; data: any }> {
    try {
      const response = await axios.post(`${API_BASE_URL}/candidates/${id}/analyze-pdf`);
      return response.data;
    } catch (error) {
      console.error('Error analyzing candidate PDF:', error);
      throw error;
    }
  }

  /**
   * Batch analyze multiple candidates
   */
  async batchAnalyzeCandidates(candidateIds: string[]): Promise<{ success: boolean; data: any }> {
    try {
      const response = await axios.post(`${API_BASE_URL}/candidates/batch-analyze`, {
        candidateIds
      });
      return response.data;
    } catch (error) {
      console.error('Error batch analyzing candidates:', error);
      throw error;
    }
  }

  /**
   * Prüft, ob die E-Mail schon einem Kandidaten gehört (Dublettenwarnung im Formular).
   */
  async pruefeEmail(email: string): Promise<VorhandenerKandidat | null> {
    const response = await axios.get(`${API_BASE_URL}/candidates/check-email`, { params: { email } });
    return response.data?.existing || null;
  }

  /**
   * Create new candidate
   */
  async createCandidate(candidateData: Partial<Candidate>): Promise<{ success: boolean; data: Candidate; message?: string }> {
    try {
      console.log('📝 Creating new candidate:', candidateData.first_name, candidateData.last_name);
      const response = await axios.post(`${API_BASE_URL}/candidates`, candidateData);
      console.log('✅ Candidate created successfully');
      return response.data;
    } catch (error: any) {
      console.error('❌ Error creating candidate:', error.response?.data || error.message);
      throw error;
    }
  }

  /**
   * Update candidate
   */
  async updateCandidate(id: string, updates: Partial<Candidate>): Promise<{ success: boolean; data: Candidate; message?: string }> {
    try {
      console.log(`📝 Updating candidate ${id}`);
      const response = await axios.put(`${API_BASE_URL}/candidates/${id}`, updates);
      console.log('✅ Candidate updated successfully');
      return response.data;
    } catch (error: any) {
      console.error('❌ Error updating candidate:', error.response?.data || error.message);
      throw error;
    }
  }

  /**
   * Delete candidate
   */
  async deleteCandidate(id: string): Promise<{ success: boolean; data: Candidate; message?: string }> {
    try {
      console.log(`🗑️ Deleting candidate ${id}`);
      const response = await axios.delete(`${API_BASE_URL}/candidates/${id}`);
      console.log('✅ Candidate deleted successfully');
      return response.data;
    } catch (error: any) {
      console.error('❌ Error deleting candidate:', error.response?.data || error.message);
      throw error;
    }
  }

  /**
   * Search candidates by PHE-ID, name, email, or phone
   */
  async searchCandidates(query: string, limit = 20): Promise<{ success: boolean; data: Candidate[]; count: number }> {
    try {
      console.log(`🔍 Searching candidates: "${query}"`);
      const response = await axios.get(`${API_BASE_URL}/candidates/search`, {
        params: { q: query, limit }
      });
      return response.data;
    } catch (error: any) {
      console.error('❌ Error searching candidates:', error.response?.data || error.message);
      throw error;
    }
  }

  /**
   * Get next available PHE candidate number
   */
  async getNextPHENumber(): Promise<{ success: boolean; data: { candidate_number: string } }> {
    try {
      const response = await axios.get(`${API_BASE_URL}/candidates/next-phe-number`);
      return response.data;
    } catch (error: any) {
      console.error('❌ Error getting next PHE number:', error.response?.data || error.message);
      throw error;
    }
  }

  /**
   * Upload document for candidate
   */
  async uploadCandidateDocument(candidateId: string, file: File): Promise<{ success: boolean; data: CandidateDocument }> {
    try {
      console.log(`📤 Uploading document for candidate ${candidateId}: ${file.name}`);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('related_to_type', 'candidate');
      formData.append('related_to_id', candidateId);

      const response = await axios.post(`${API_BASE_URL}/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      console.log('✅ Document uploaded successfully');
      return response.data;
    } catch (error: any) {
      console.error('❌ Error uploading document:', error.response?.data || error.message);
      throw error;
    }
  }

  /**
   * Delete candidate document
   */
  async deleteCandidateDocument(documentId: string): Promise<{ success: boolean; message: string }> {
    try {
      console.log(`🗑️ Deleting document ${documentId}`);
      const response = await axios.delete(`${API_BASE_URL}/documents/${documentId}`);
      console.log('✅ Document deleted successfully');
      return response.data;
    } catch (error: any) {
      console.error('❌ Error deleting document:', error.response?.data || error.message);
      throw error;
    }
  }

  /**
   * Get document download URL
   */
  getDocumentDownloadUrl(documentId: string): string {
    return `${API_BASE_URL}/candidates/documents/${documentId}/download`;
  }

  /**
   * Check if a name looks like a company name
   */
  isCompanyName(name: string): boolean {
    const companyIndicators = [
      'GmbH', 'AG', 'KG', 'SE', 'UG', 'e.V.', 'e. V.',
      'GmbH & Co.', 'GmbH & Co. KG', 'AG & Co.', 'OHG',
      'Limited', 'Ltd', 'Inc', 'Corp', 'Corporation',
      'Gesellschaft', 'Unternehmen', 'Firma'
    ];

    return companyIndicators.some(indicator =>
      name.includes(indicator)
    );
  }

  /**
   * Convert Candidate to Contact format for UI compatibility
   */
  candidateToContact(candidate: Candidate) {
    // AI decides: Position if available, otherwise Education (Ausbildung)
    let displayPosition = candidate.current_position;

    if (!displayPosition && candidate.education && candidate.education.length > 0) {
      // Use the most recent education (degree)
      const latestEducation = candidate.education[0];
      displayPosition = latestEducation.degree || latestEducation.field || 'Ausbildung';
    }

    if (!displayPosition) {
      displayPosition = 'Kandidat';
    }

    const firstName = candidate.first_name || '';
    const lastName = candidate.last_name || '';

    return {
      id: candidate.id,
      name: `${firstName} ${lastName}`.trim() || 'Unbekannt',
      email: candidate.email || 'N/A',
      phone: candidate.phone || 'N/A',
      company: candidate.current_company || 'N/A',
      position: displayPosition,
      avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${firstName}${lastName}`,
      location: [candidate.city, candidate.country].filter(Boolean).join(', ') || 'N/A',
      tags: (candidate.skills || []).slice(0, 3), // First 3 skills as tags
      isFavorite: candidate.is_favorite || false,
      lastContact: 'N/A',
      type: 'people' as const,
      notes: candidate.ai_summary || candidate.notes || '',
      // Add candidate-specific data
      candidateData: candidate
    };
  }
}

export const candidatesService = new CandidatesService();

// Default export for compatibility
export default candidatesService;
